import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import api from "@/lib/api";
import { Contact, Package, Building2, Users, ArrowRight, Loader2 } from "lucide-react";

const TYPE_ICON = { customer: Contact, product: Package, supplier: Building2, personnel: Users };

export default function CommandPalette({ open, setOpen, navItems = [] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const seq = useRef(0);

  useEffect(() => { if (!open) { setQuery(""); setResults([]); setLoading(false); } }, [open]);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const my = ++seq.current;
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/global", { params: { q } });
        if (my === seq.current) setResults(data?.results || []);
      } catch { if (my === seq.current) setResults([]); }
      finally { if (my === seq.current) setLoading(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [query]);

  const go = (path) => { setOpen(false); setQuery(""); navigate(path); };

  const ql = query.trim().toLowerCase();
  const filteredNav = ql ? navItems.filter((n) => (n.label || "").toLowerCase().includes(ql)) : navItems;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl overflow-hidden p-0" data-testid="command-palette">
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-slate-400">
          <CommandInput data-testid="command-input" value={query} onValueChange={setQuery}
            placeholder="Ara: sayfa, müşteri, ürün, tedarikçi, personel…" />
          <CommandList className="max-h-[60vh]">
            {loading && (
              <div className="flex items-center justify-center gap-2 py-4 text-xs text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Aranıyor…
              </div>
            )}
            {!loading && <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>}

            {filteredNav.length > 0 && (
              <CommandGroup heading="Sayfalar">
                {filteredNav.slice(0, ql ? 8 : 40).map((n) => {
                  const Icon = n.icon;
                  return (
                    <CommandItem key={n.to} value={`nav-${n.to}`} onSelect={() => go(n.to)}
                      data-testid={`cmd-nav-${n.to}`} className="cursor-pointer gap-2.5">
                      {Icon && <Icon className="h-4 w-4 text-slate-500" />}
                      <span className="text-sm text-slate-700">{n.label}</span>
                      <ArrowRight className="ml-auto h-3.5 w-3.5 text-slate-300" />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {results.length > 0 && (
              <CommandGroup heading="Kayıtlar">
                {results.map((r) => {
                  const Icon = TYPE_ICON[r.type] || Contact;
                  return (
                    <CommandItem key={`${r.type}-${r.id}`} value={`${r.type}-${r.id}`} onSelect={() => go(r.link)}
                      data-testid={`cmd-result-${r.type}-${r.id}`} className="cursor-pointer gap-2.5">
                      <Icon className="h-4 w-4 text-brand-green" />
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-sm text-slate-800">{r.label}</span>
                        <span className="truncate text-[11px] text-slate-400">{r.type_label}{r.sub ? ` · ${r.sub}` : ""}</span>
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
